import { AuroraControlId, type ClientDispatchedToolResult } from 'api-validations/aurora'
import { useAuroraContextSlot } from 'shared-ui/aurora'
import { Enum } from 'utils'
import { create } from 'zustand'

export enum AwaitKind {
  Answer = 'answer',
  Press = 'press'
}

export type Awaits = { kind: AwaitKind.Answer; satisfied: boolean } | { kind: AwaitKind.Press }

type OfferedControl = {
  value: string
  awaits?: Awaits
}

type AuroraControlsState = {
  offered: Partial<Record<AuroraControlId, OfferedControl>>
  shown: AuroraControlId[]
  awaiting: AuroraControlId[]
  slots: Partial<Record<AuroraControlId, HTMLElement>>
}

export const useAuroraControlsStore = create<AuroraControlsState>(() => ({
  offered: {},
  shown: [],
  awaiting: [],
  slots: {}
}))

const without = (ids: AuroraControlId[], removed: AuroraControlId[]) =>
  ids.filter((id) => !removed.includes(id))

const isUnanswered = (control: OfferedControl | undefined) =>
  control?.awaits?.kind === AwaitKind.Answer && !control.awaits.satisfied

const isAwaited = (control: OfferedControl | undefined) =>
  isUnanswered(control) || control?.awaits?.kind === AwaitKind.Press

const slotRefs = new Map<AuroraControlId, (node: HTMLElement | null) => void>()
const reportListeners = new Set<(report: string) => void>()

export const auroraControls = {
  offer: (id: AuroraControlId, control: OfferedControl) =>
    useAuroraControlsStore.setState((state) => ({ offered: { ...state.offered, [id]: control } })),

  withdraw: (id: AuroraControlId) =>
    useAuroraControlsStore.setState((state) => {
      const { [id]: _withdrawn, ...offered } = state.offered
      return {
        offered,
        shown: without(state.shown, [id]),
        awaiting: without(state.awaiting, [id])
      }
    }),

  show: (requested: AuroraControlId[]): ClientDispatchedToolResult<'ShowControls'> => {
    const { offered } = useAuroraControlsStore.getState()
    const controls = [...new Set(requested)]
    const shown = controls.filter((id) => offered[id] !== undefined)
    const awaiting = shown.filter((id) => isAwaited(offered[id]))
    useAuroraControlsStore.setState({ shown, awaiting })
    return {
      shown,
      awaitingUser: awaiting,
      unavailable: controls.filter((id) => offered[id] === undefined)
    }
  },

  hide: (id: AuroraControlId) =>
    useAuroraControlsStore.setState((state) => ({
      shown: without(state.shown, [id]),
      awaiting: without(state.awaiting, [id])
    })),

  continueAwaiting: (): string => {
    const { offered, awaiting } = useAuroraControlsStore.getState()
    const answered = awaiting.filter((id) => offered[id]?.awaits?.kind === AwaitKind.Answer)
    useAuroraControlsStore.setState((state) => ({
      shown: without(state.shown, answered),
      awaiting: without(state.awaiting, answered)
    }))
    return answered.map((id) => offered[id]?.value ?? '').join('\n')
  },

  stopAwaiting: () => useAuroraControlsStore.setState({ awaiting: [] }),

  reset: () => useAuroraControlsStore.setState({ shown: [], awaiting: [] }),

  report: ({ control, report }: { control: AuroraControlId; report: string }) => {
    if (!useAuroraControlsStore.getState().shown.includes(control)) return
    reportListeners.forEach((listener) => listener(report))
  },

  onReport: (listener: (report: string) => void) => {
    reportListeners.add(listener)
    return () => {
      reportListeners.delete(listener)
    }
  },

  slotRef: (id: AuroraControlId) => {
    const existing = slotRefs.get(id)
    if (existing !== undefined) return existing
    const ref = (node: HTMLElement | null) =>
      useAuroraControlsStore.setState((state) => {
        const { [id]: _previous, ...slots } = state.slots
        return { slots: node === null ? slots : { ...slots, [id]: node } }
      })
    slotRefs.set(id, ref)
    return ref
  }
}

export const awaitsAnswer = ({ offered, awaiting }: AuroraControlsState) =>
  awaiting.some((id) => offered[id]?.awaits?.kind === AwaitKind.Answer)

export const isAwaitingSatisfied = ({ offered, awaiting }: AuroraControlsState) =>
  awaiting.every((id) => !isUnanswered(offered[id]))

export const useAuroraControlsContext = () => {
  const offered = useAuroraControlsStore((state) => state.offered)
  const shown = useAuroraControlsStore((state) => state.shown)
  const controls = Enum.values(AuroraControlId).flatMap((control) => {
    const offer = offered[control]
    if (offer === undefined) return []
    return [{ control, value: offer.value, shown: shown.includes(control) }]
  })
  useAuroraContextSlot('auroraControls', controls.length === 0 ? undefined : controls)
}
